import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as Highcharts from 'highcharts/highstock';
import { isEqual } from 'lodash-es';
import { Subject, interval } from 'rxjs';
import { buffer, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import {
  RemoteDiagnosticsResponse,
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVddGenericStructure,
} from '@vi-iot/client';

import {
  RemoteDiagnosticsChartValue,
  RemoteDiagnosticsParameter,
  RemoteDiagnosticsStore,
  VddDetailsListStore,
} from '@store';

import { DiagnosticsChartService } from '../diagnostics-chart.service';
import { NameValues } from '../diagnostics-constants';
import { DiagnosticsHelpers } from '../diagnostics-helpers';
import { DiagnosticsWebsocketService } from '../diagnostics-websocket.service';

@Component({
  selector: 'app-diagnostics-chart',
  templateUrl: './diagnostics-chart.component.html',
  styleUrls: ['./diagnostics-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticsChartComponent implements OnInit, OnDestroy {
  highcharts: typeof Highcharts = Highcharts;
  parametersPreviousValue: RemoteDiagnosticsParameter[] = [];
  gatewaySerial: string;
  vddSensors: RemoteDiagnosticsParameter[] = [];

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private diagnosticsWebsocketService: DiagnosticsWebsocketService<
      RemoteDiagnosticsSuccessResponseData
    >,
    private vddDetailsListStore: VddDetailsListStore,
    public diagnosticsChartService: DiagnosticsChartService,
    public activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.gatewaySerial = this.activatedRoute.snapshot.queryParams['gatewaySerial'];
    this.seriesManagementSubscription();
    this.dataManagementSubscription();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.diagnosticsChartService.removeAllSeries();
  }

  private seriesManagementSubscription(): void {
    this.remoteDiagnosticsStore
      .getParametersWhichAreBeingRead(this.gatewaySerial)
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((parameters: RemoteDiagnosticsParameter[]) => {
        this.vddSensors = [];
        if (this.parametersPreviousValue?.length < parameters?.length) {
          parameters.forEach((currentParameter, index) => {
            const parameterExists = this.parametersPreviousValue.find(
              (existingParameter) => existingParameter.id === currentParameter.id
            );

            this.getVddSensor(currentParameter, parameterExists, index === parameters.length - 1);
          });
        }
        this.parametersPreviousValue = parameters;
      });
  }

  private dataManagementSubscription(): void {
    this.diagnosticsWebsocketService.webSocketValues
      .pipe(
        filter((wsData: RemoteDiagnosticsResponse<RemoteDiagnosticsSuccessResponseData>) =>
          this.isVddSensorFromWebsocket(wsData)
        ),
        map((wsData: RemoteDiagnosticsResponse<RemoteDiagnosticsSuccessResponseData>) =>
          this.diagnosticsWebsocketService.extractSensorValuesFromWebsocketResponse(wsData)
        ),
        // we render chart every second with data accumulated from websocket - re rendering on each message was a perfornamce kill
        buffer(interval(1000)),
        distinctUntilChanged(isEqual), // we dont trigger chart re-render if there is no new data
        takeUntil(this.ngUnsubscribe$)
      )

      .subscribe((chartsValue: RemoteDiagnosticsChartValue[]) => {
        this.diagnosticsChartService.addData(chartsValue);
      });
  }

  updateChange(updateReset: boolean): void {
    this.diagnosticsChartService.updateFlag.next(updateReset);
  }

  getVddSensor(
    currentParameter: RemoteDiagnosticsParameter,
    parameterExists: RemoteDiagnosticsParameter,
    isLastItem: boolean
  ): void {
    this.vddDetailsListStore
      .getVddDetail(currentParameter.vddDetail.name, currentParameter.controlUnit.value)
      .pipe(takeUntil(this.ngUnsubscribe$), distinctUntilChanged(isEqual))
      .subscribe((value) => {
        const isVddSensor = DiagnosticsHelpers.isVddSensorFromStructure(value?.structure);

        if (!parameterExists && isVddSensor) {
          this.vddSensors = [...this.vddSensors, currentParameter];
        }

        if (isLastItem) {
          this.addSeries();
        }
      });
  }

  addSeries(): void {
    this.vddSensors.forEach((vddSensor) => {
      this.diagnosticsChartService.addSeries(
        vddSensor.requestId,
        vddSensor.vddDetail.name,
        this.diagnosticsChartService.getLLName(vddSensor),
        this.vddSensors.filter((vs) => vs.vddDetail.name === vddSensor.vddDetail.name).length > 1
      );
    });
  }

  private isVddSensorFromWebsocket(
    vdd: RemoteDiagnosticsResponse<RemoteDiagnosticsSuccessResponseData>
  ): boolean {
    const dataRecordStructure = vdd.data.structure.find(
      (dataStructure: RemoteDiagnosticsVddGenericStructure) =>
        dataStructure.path === NameValues.DataRecor
    );

    return dataRecordStructure ? DiagnosticsHelpers.isVddSensor(dataRecordStructure) : false;
  }
}
